(() => {
  if (window.OttuniChairProduct) {
    window.OttuniChairProduct.scan();
    return;
  }

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const pad = (value) => String(value).padStart(2, "0");

  const setGalleryMedia = (root, mediaId) => {
    const gallery = root.querySelector("[data-ocp-gallery]");
    if (!gallery) return;

    const mediaItems = [...gallery.querySelectorAll("[data-ocp-media]")];
    const thumbnails = [...gallery.querySelectorAll("[data-ocp-gallery-thumb]")];
    const target = mediaItems.find((item) => String(item.dataset.ocpMedia) === String(mediaId));
    if (!target) return;

    const current = mediaItems.find((item) => item.classList.contains("is-active"));
    const activate = () => {
      mediaItems.forEach((item) => {
        const active = item === target;
        item.hidden = !active;
        item.classList.toggle("is-active", active);
        if (!active) item.querySelector("video")?.pause();
      });

      thumbnails.forEach((button) => {
        const active = String(button.dataset.ocpGalleryThumb) === String(mediaId);
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });

      const index = Number(target.dataset.ocpMediaIndex || 1);
      const counter = gallery.querySelector("[data-ocp-gallery-counter]");
      if (counter) counter.textContent = `${pad(index)} / ${pad(mediaItems.length)}`;
    };

    if (!current || current === target || reducedMotion.matches) {
      activate();
      return;
    }

    current.classList.remove("is-active");
    window.setTimeout(activate, 130);
  };

  const initMain = (root) => {
    if (root.dataset.ocpReady === "true") return;
    root.dataset.ocpReady = "true";

    const variantsScript = root.querySelector("[data-ocp-variants]");
    let variants = [];
    try {
      variants = JSON.parse(variantsScript?.textContent || "[]");
    } catch {
      variants = [];
    }

    const mediaItems = [...root.querySelectorAll("[data-ocp-media]")];
    const thumbnails = [...root.querySelectorAll("[data-ocp-gallery-thumb]")];
    const previous = root.querySelector("[data-ocp-gallery-prev]");
    const next = root.querySelector("[data-ocp-gallery-next]");

    const currentMediaIndex = () => {
      const active = mediaItems.find((item) => item.classList.contains("is-active"));
      return Math.max(0, mediaItems.indexOf(active));
    };

    thumbnails.forEach((button) => {
      button.addEventListener("click", () => setGalleryMedia(root, button.dataset.ocpGalleryThumb));
    });

    previous?.addEventListener("click", () => {
      if (!mediaItems.length) return;
      const index = (currentMediaIndex() - 1 + mediaItems.length) % mediaItems.length;
      setGalleryMedia(root, mediaItems[index].dataset.ocpMedia);
    });

    next?.addEventListener("click", () => {
      if (!mediaItems.length) return;
      const index = (currentMediaIndex() + 1) % mediaItems.length;
      setGalleryMedia(root, mediaItems[index].dataset.ocpMedia);
    });

    const optionInputs = [...root.querySelectorAll("[data-ocp-option-input]")];
    const variantId = root.querySelector("[data-ocp-variant-id]");
    const price = root.querySelector("[data-ocp-price]");
    const compare = root.querySelector("[data-ocp-compare]");
    const availability = root.querySelector("[data-ocp-availability]");
    const addButton = root.querySelector("[data-ocp-add]");
    const quantity = root.querySelector("[data-ocp-quantity]");
    const quantityMinus = root.querySelector("[data-ocp-quantity-minus]");
    const quantityPlus = root.querySelector("[data-ocp-quantity-plus]");
    const sku = root.querySelector("[data-ocp-sku]");

    const selectedOptions = () => {
      const groups = [...new Set(optionInputs.map((input) => Number(input.dataset.optionPosition)))].sort((a, b) => a - b);
      return groups.map((position) => {
        return root.querySelector(`[data-ocp-option-input][data-option-position="${position}"]:checked`)?.value || "";
      });
    };

    const syncOptionLabels = () => {
      optionInputs.forEach((input) => {
        if (!input.checked) return;
        const output = root.querySelector(`[data-ocp-option-name="${input.dataset.optionPosition}"]`);
        if (output) output.textContent = input.value;
      });
    };

    const syncOptionAvailability = () => {
      const selected = selectedOptions();
      optionInputs.forEach((input) => {
        const position = Number(input.dataset.optionPosition) - 1;
        const possible = variants.some((variant) => {
          if (!variant.available || variant.options[position] !== input.value) return false;
          return variant.options.every((value, index) => index === position || value === selected[index]);
        });
        input.disabled = !possible && !input.checked;
      });
    };

    const updateVariant = () => {
      const selected = selectedOptions();
      const variant = variants.find((item) => {
        return item.options.length === selected.length && item.options.every((value, index) => value === selected[index]);
      });

      syncOptionLabels();
      syncOptionAvailability();

      if (!variant) {
        if (variantId) variantId.value = "";
        if (addButton) {
          addButton.disabled = true;
          addButton.textContent = addButton.dataset.unavailableLabel || "Unavailable";
        }
        if (availability) availability.textContent = availability.dataset.unavailableLabel || "Unavailable";
        return;
      }

      if (variantId) variantId.value = variant.id;
      if (price) price.textContent = variant.price;

      if (compare) {
        compare.textContent = variant.compareAtHigher ? variant.compareAt || "" : "";
        compare.classList.toggle("is-hidden", !variant.compareAtHigher);
      }

      if (availability) {
        availability.textContent = variant.available
          ? availability.dataset.availableLabel || "Available"
          : availability.dataset.soldLabel || "Sold out";
      }

      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available
          ? addButton.dataset.addLabel || "Add to cart"
          : addButton.dataset.soldLabel || "Sold out";
      }

      if (quantity) {
        quantity.min = variant.quantityMin || 1;
        quantity.step = variant.quantityIncrement || 1;
        if (variant.quantityMax) {
          quantity.max = variant.quantityMax;
        } else {
          quantity.removeAttribute("max");
        }
        const currentQuantity = Number(quantity.value || variant.quantityMin || 1);
        quantity.value = Math.max(Number(variant.quantityMin || 1), currentQuantity);
      }

      if (sku) {
        const value = sku.querySelector("span");
        if (value) value.textContent = variant.sku || "";
        sku.classList.toggle("is-hidden", !variant.sku);
      }

      if (variant.featuredMediaId) setGalleryMedia(root, variant.featuredMediaId);

      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url);

      document.dispatchEvent(new CustomEvent("ottuni:variant-change", { detail: { variant } }));
    };

    optionInputs.forEach((input) => input.addEventListener("change", updateVariant));
    quantityMinus?.addEventListener("click", () => {
      if (!quantity) return;
      const step = Number(quantity.step || 1);
      const min = Number(quantity.min || 1);
      quantity.value = Math.max(min, Number(quantity.value || min) - step);
      quantity.dispatchEvent(new Event("change", { bubbles: true }));
    });
    quantityPlus?.addEventListener("click", () => {
      if (!quantity) return;
      const step = Number(quantity.step || 1);
      const max = quantity.max ? Number(quantity.max) : Number.POSITIVE_INFINITY;
      quantity.value = Math.min(max, Number(quantity.value || 1) + step);
      quantity.dispatchEvent(new Event("change", { bubbles: true }));
    });
    syncOptionLabels();
    syncOptionAvailability();
  };

  const initProductNavigation = (root) => {
    if (root.dataset.pdpNavigationReady === "true") return;
    root.dataset.pdpNavigationReady = "true";

    const toggle = root.querySelector("[data-pdp-menu-toggle]");
    const navigation = root.querySelector("[data-pdp-site-nav]");
    if (!toggle || !navigation) return;

    const close = () => {
      root.classList.remove("is-open");
      toggle.setAttribute("aria-expanded", "false");
    };

    toggle.addEventListener("click", () => {
      const open = root.classList.toggle("is-open");
      toggle.setAttribute("aria-expanded", String(open));
    });
    navigation.addEventListener("click", (event) => {
      if (event.target.closest("a")) close();
    });
  };

  const initMaterials = (root) => {
    if (root.dataset.ocpMaterialsReady === "true") return;
    root.dataset.ocpMaterialsReady = "true";

    const tabs = [...root.querySelectorAll("[data-ocp-material-tab]")];
    const images = [...root.querySelectorAll("[data-ocp-material-image]")];

    const activate = (index, focus = false) => {
      tabs.forEach((tab) => {
        const active = Number(tab.dataset.ocpMaterialTab) === index;
        tab.classList.toggle("is-active", active);
        tab.setAttribute("aria-selected", String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active && focus) tab.focus();
      });

      images.forEach((image) => {
        const active = Number(image.dataset.ocpMaterialImage) === index;
        image.hidden = !active;
        image.classList.toggle("is-active", active);
      });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => activate(Number(tab.dataset.ocpMaterialTab)));
      tab.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
        event.preventDefault();
        let nextIndex = index;
        if (["ArrowLeft", "ArrowUp"].includes(event.key)) nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (["ArrowRight", "ArrowDown"].includes(event.key)) nextIndex = (index + 1) % tabs.length;
        if (event.key === "Home") nextIndex = 0;
        if (event.key === "End") nextIndex = tabs.length - 1;
        activate(Number(tabs[nextIndex].dataset.ocpMaterialTab), true);
      });
    });

    if (tabs.length) activate(Number(tabs[0].dataset.ocpMaterialTab));
  };

  const initFit = (root) => {
    if (root.dataset.ocpFitReady === "true") return;
    root.dataset.ocpFitReady = "true";

    const tabs = [...root.querySelectorAll("[data-ocp-fit-tab]")];
    const views = [...root.querySelectorAll("[data-ocp-fit-view]")];

    const activate = (key, focus = false) => {
      tabs.forEach((tab) => {
        const active = tab.dataset.ocpFitTab === key;
        tab.classList.toggle("is-active", active);
        tab.setAttribute("aria-selected", String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active && focus) tab.focus();
      });

      views.forEach((view) => {
        const active = view.dataset.ocpFitView === key;
        view.hidden = !active;
        view.classList.toggle("is-active", active);
      });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => activate(tab.dataset.ocpFitTab));
      tab.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        event.preventDefault();
        let nextIndex = index;
        if (event.key === "ArrowLeft") nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
        if (event.key === "Home") nextIndex = 0;
        if (event.key === "End") nextIndex = tabs.length - 1;
        activate(tabs[nextIndex].dataset.ocpFitTab, true);
      });
    });

    if (tabs.length) activate(tabs[0].dataset.ocpFitTab);
  };

  const initMotion = (video) => {
    if (video.dataset.ocpMotionReady === "true") return;
    video.dataset.ocpMotionReady = "true";

    if (reducedMotion.matches) {
      video.autoplay = false;
      video.pause();
    }

    const toggle = video.closest(".function-film__media")?.querySelector("[data-ocp-motion-toggle]");
    const label = toggle?.querySelector("[data-ocp-motion-label]");
    const syncToggle = () => {
      if (!toggle || !label) return;
      const paused = video.paused;
      toggle.setAttribute("aria-pressed", String(!paused));
      label.textContent = paused
        ? toggle.dataset.playLabel || "Play demonstration"
        : toggle.dataset.pauseLabel || "Pause demonstration";
    };

    toggle?.addEventListener("click", () => {
      if (video.paused) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
    video.addEventListener("play", syncToggle);
    video.addEventListener("pause", syncToggle);
    syncToggle();

    if (reducedMotion.matches) return;

    if (!video.hasAttribute("autoplay") || !("IntersectionObserver" in window)) return;

    let inView = false;
    let automated = false;
    let pausedByUser = false;

    video.addEventListener("pause", () => {
      if (!automated && inView) pausedByUser = true;
    });

    video.addEventListener("play", () => {
      if (!automated) pausedByUser = false;
    });

    const observer = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        automated = true;
        if (inView && !pausedByUser) {
          video.play().catch(() => {});
        } else if (!inView) {
          video.pause();
        }
        window.setTimeout(() => {
          automated = false;
        }, 0);
      },
      { threshold: 0.35 }
    );

    observer.observe(video);
  };

  const scan = (scope = document) => {
    scope.querySelectorAll("[data-product-navigation]").forEach(initProductNavigation);
    scope.querySelectorAll("[data-ottuni-chair-main]").forEach(initMain);
    scope.querySelectorAll("[data-ocp-materials]").forEach(initMaterials);
    scope.querySelectorAll("[data-ocp-fit]").forEach(initFit);
    scope.querySelectorAll("[data-ocp-motion-video]").forEach(initMotion);
  };

  document.addEventListener("ottuni:variant-change", (event) => {
    document.querySelectorAll("[data-ocp-spec-sku]").forEach((output) => {
      output.textContent = event.detail.variant.sku || "";
      output.closest("div")?.classList.toggle("is-hidden", !event.detail.variant.sku);
    });
  });

  document.addEventListener("shopify:section:load", (event) => scan(event.target));
  window.OttuniChairProduct = { scan };
  scan();
})();
